/**
 * Umumiy UI komponentlari — Monoline kit ustidagi yupqa moslashtiruvchi qatlam.
 *
 * Bu fayl endi o'z ko'rinishini chizmaydi: har bir eksport `src/ui/` dagi mos
 * kit komponentiga (yoki uning `ui-` sinfiga) o'tkazadi. Eksport nomlari va
 * proplari o'zgarmagan — shuning uchun hali ko'chirilmagan sahifalar ham
 * hech qanday tahrirsiz yangi ko'rinishga o'tadi.
 *
 * Monoxrom tizim qoidasi (DESIGN.md 1-bo'lim): rang faqat ma'lumot tashiydi.
 * Shu sababli chaqiruvchi bergan bezak proplari — `color`, `bg`, `grad`,
 * `countBg`, `countColor`, `height` — qabul qilinadi, lekin qo'llanmaydi.
 * Ular imzoni buzmaslik uchun saqlanib turibdi.
 */
import { HiArrowTrendingDown, HiArrowTrendingUp, HiClock } from 'react-icons/hi2'
import {
  Badge as KitBadge,
  Card as KitCard,
  ConfirmModal,
  Dot,
  Modal as KitModal,
  Pagination as KitPagination,
  Progress,
  Switch,
  Tabs as KitTabs,
  Toolbar,
  cx,
} from '../ui'

/**
 * Ikonka proplari tarixan ham komponent (`HiFire`), ham tayyor tugun
 * (`<HiFire />`) sifatida uzatilgan — ikkalasini ham chizadi.
 */
function renderIcon(icon) {
  if (!icon) return null
  if (typeof icon === 'function') {
    const Icon = icon
    return <Icon aria-hidden="true" />
  }
  return icon
}

/* StatCard — kitning KPI ko'rinishi (ui-kpi) */
export function StatCard({ label, val, icon, color: _color, bg: _bg, trend, up }) {
  // color/bg qabul qilinadi-yu ishlatilmaydi: KPI kartasi neytral yuzada turadi.
  return (
    <div className="ui-kpi">
      <div className="ui-kpi__value ui-nums">{val}</div>
      <div className="ui-kpi__label">
        {renderIcon(icon)}
        {label}
      </div>
      {trend && (
        <div className={cx('ui-kpi__delta', 'ui-nums', up ? 'is-up' : 'is-down')}>
          {up ? <HiArrowTrendingUp aria-hidden="true" /> : <HiArrowTrendingDown aria-hidden="true" />}
          {trend}
        </div>
      )}
    </div>
  )
}

/* Badge — eski `type` so'z boyligi kit ohanglariga o'tkaziladi */
const BADGE_TONE = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  online: 'ok',
  ok: 'ok',
  busy: 'high',
  // Bosqich yorliqlari (Yangi / Qabul / Jarayonda / Yopildi) — DESIGN.md 4-bo'lim
  // bo'yicha doim neytral: bu shoshilinchlik emas, faqat holat.
  new: 'neutral',
  accepted: 'neutral',
  progress: 'neutral',
  waiting: 'neutral',
  resolved: 'neutral',
  closed: 'neutral',
  offline: 'neutral',
}

export function Badge({ type = 'new', tone, children, ...rest }) {
  return (
    <KitBadge tone={tone || BADGE_TONE[type] || 'neutral'} {...rest}>
      {children}
    </KitBadge>
  )
}

/* Avatar — kitning `ui-avatar` yuzasi, ichida bosh harflar */
function avatarFontSize(size) {
  if (size >= 36) return 'var(--ui-fs-md)'
  if (size >= 28) return 'var(--ui-fs-sm)'
  return 'var(--ui-fs-2xs)'
}

export function Avatar({ initials, grad: _grad, size = 38 }) {
  // grad (gradient) qo'llanmaydi — monoxrom tizimda avatar neytral yuzada turadi.
  return (
    <span
      className="ui-avatar"
      style={{ width: size, height: size, fontSize: avatarFontSize(size) }}
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}

/* StatusDot */
const DOT_TONE = { online: 'ok', busy: 'busy', offline: 'off' }

export function StatusDot({ status }) {
  return <Dot tone={DOT_TONE[status] || 'off'} />
}

/* OpStatusBadge — Badge + Dot (holat faqat rang bilan berilmaydi) */
const OP_TONE = { online: 'ok', busy: 'high', offline: 'neutral' }
const OP_LABEL = { online: 'Onlayn', busy: 'Band', offline: 'Oflayn' }

export function OpStatusBadge({ status }) {
  return (
    <KitBadge tone={OP_TONE[status] || 'neutral'}>
      <Dot tone={DOT_TONE[status] || 'off'} />
      {OP_LABEL[status] || status}
    </KitBadge>
  )
}

/* ProgressBar */
export function ProgressBar({ value, color: _color, height: _height, tone }) {
  // color/height e'tiborga olinmaydi: chiziq balandligi va rangi kit tokenlarida
  // qat'iy belgilangan. Status kerak bo'lsa `tone` orqali beriladi.
  return <Progress value={value} tone={tone} />
}

/* Toggle */
export function Toggle({ on, onChange, label }) {
  return (
    <Switch
      checked={!!on}
      onChange={(e) => onChange?.(e.target.checked)}
      aria-label={label || "Yoqish/o'chirish"}
    />
  )
}

/* SettingsRow — kalit/qiymat qatori (ui-dl__row) */
export function SettingsRow({ label, sub, children }) {
  return (
    <div className="ui-dl__row">
      <div className="ui-dl__key">
        <div style={{ fontSize: 'var(--ui-fs-sm)', fontWeight: 500, color: 'var(--ui-text-1)' }}>
          {label}
        </div>
        {sub && <div style={{ marginTop: 4 }}>{sub}</div>}
      </div>
      <div className="ui-dl__val">{children}</div>
    </div>
  )
}

/* Pagination — bitta sahifa qolganda ko'rinmaydi (eski xulq saqlanadi) */
export function Pagination({ total, perPage = 20, page = 1, onChange = () => {} }) {
  const pages = Math.ceil(total / perPage)
  if (pages <= 1) return null
  return <KitPagination page={page} total={total} perPage={perPage} onChange={onChange} />
}

/* Modal */
export function Modal({ open, onClose, title, children, footer, width }) {
  return (
    <KitModal
      open={open}
      onClose={onClose}
      title={title}
      footer={footer}
      style={width ? { width, maxWidth: '96vw' } : undefined}
    >
      {children}
    </KitModal>
  )
}

/* Card */
export function Card({ title, actions, children, style, icon, ...rest }) {
  return (
    <KitCard title={title} actions={actions} icon={icon} style={style} {...rest}>
      {children}
    </KitCard>
  )
}

/* Tabs — eski `tabs/active` imzosi kitning `items/value` iga o'tkaziladi */
export function Tabs({ tabs = [], active, onChange }) {
  const items = tabs.map((tab) => ({
    id: tab.id,
    // Ikonka tugun sifatida kelgan bo'lsa (eski uslub) — yorliq matniga qo'shiladi,
    // komponent bo'lsa kitning o'z `icon` proiga uzatiladi.
    icon: typeof tab.icon === 'function' ? tab.icon : undefined,
    label:
      tab.icon && typeof tab.icon !== 'function' ? (
        <>
          {tab.icon}
          {tab.label}
        </>
      ) : (
        tab.label
      ),
    count: tab.count,
  }))
  return <KitTabs items={items} value={active} onChange={onChange} />
}

/* FilterBar */
export function FilterBar({ children }) {
  return <Toolbar>{children}</Toolbar>
}

/* AuditRow — vaqt lentasining bitta qatori (ui-timeline__row) */
export function AuditRow({ ic, bg: _bg, user, action, obj, ip, time }) {
  // bg qo'llanmaydi; ic faqat komponent yoki tugun bo'lsa chiziladi
  // (emoji satrlari o'rniga neytral hi2 ikonkasi turadi — DESIGN.md 8-bo'lim).
  const icon = typeof ic === 'string' ? null : renderIcon(ic)
  return (
    <div className="ui-timeline__row">
      <span className="ui-timeline__icon">{icon || <HiClock aria-hidden="true" />}</span>
      <div className="ui-timeline__body ui-grow">
        <div>
          <span style={{ fontWeight: 500 }}>{user}</span> — {action}
        </div>
        {obj && obj !== '—' && (
          <div className="ui-muted" style={{ fontSize: 'var(--ui-fs-xs)' }}>
            {obj} · IP: <span className="ui-mono">{ip}</span>
          </div>
        )}
      </div>
      {time && (
        <time className="ui-muted ui-nums" style={{ fontSize: 'var(--ui-fs-xs)', flexShrink: 0 }}>
          {time}
        </time>
      )}
    </div>
  )
}

/* ConfirmDeleteModal — nomni yozdirib tasdiqlatadigan kit oynasi */
export function ConfirmDeleteModal({ open, onClose, onConfirm, itemName, title = "O'chirishni tasdiqlang" }) {
  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={() => {
        onConfirm()
        onClose()
      }}
      title={title}
      text={`${itemName} butunlay o'chiriladi.`}
      confirmWord={itemName}
      confirmLabel="O'chirish"
      cancelLabel="Bekor qilish"
      danger
    />
  )
}
